"use client"

import { graphql, useFragment } from "@/gql"
import { MobileMediaCarouselFragmentDoc, ProjectMobileContentFragment } from "@/gql/graphql"
import useEmblaCarousel from "embla-carousel-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import clsx from "clsx"
import { Image, VideoHandle } from "@/elements"
import { Video } from "@/components"

graphql(`
  fragment MobileMediaCarousel on CreativeWorkPortfolioProject {
    id
    files {
      edges {
        node {
          id
          thumbnailUrl645w: thumbnailUrl(width: 645)
          metadata {
            video
          }
          ...Video
        }
      }
    }
  }
`)

interface Props {
  project: ProjectMobileContentFragment | undefined | null
}

export const MobileMediaCarousel: React.FC<Props> = props => {
  const project = useFragment(MobileMediaCarouselFragmentDoc, props.project)
  const [visibleIndex, setVisibleIndex] = useState(0)
  const videoRef = useRef<(VideoHandle | null)[]>([])

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    inViewThreshold: 0.6,
    active: !!project?.files?.edges && project?.files?.edges.length > 1,
  })

  const indicatorWidth = useMemo(() => {
    return 120 / (project?.files?.edges?.length || 1)
  }, [project])

  const pauseAllVideos = useCallback(() => {
    videoRef.current.forEach(video => {
      video?.pause()
    })
  }, [])

  const logSlidesInView = useCallback(
    (emblaApi: any) => {
      const inView = emblaApi.slidesInView()
      pauseAllVideos()
      const index = inView[0]
      if (typeof index === "number") {
        setVisibleIndex(index)
      }
    },
    [pauseAllVideos]
  )

  useEffect(() => {
    if (emblaApi) emblaApi.on("slidesInView", logSlidesInView)
  }, [emblaApi, logSlidesInView])

  return (
    <>
      <div className="relative">
        <div className="h-full w-full overflow-hidden" ref={emblaRef}>
          <div className="flex h-full w-full">
            {project?.files?.edges?.map((edge, index) => {
              return (
                <div
                  key={edge?.node?.id || index}
                  className="flex-basis-full h-full w-full min-w-0 flex-shrink-0 flex-grow-0">
                  {edge?.node && (
                    <div className="relative h-[350px] w-full">
                      {edge?.node?.metadata?.video ? (
                        <Video
                          video={edge.node}
                          ref={el => {
                            videoRef.current[index] = el
                          }}
                          poster={edge?.node?.thumbnailUrl645w ?? ""}
                          controls
                        />
                      ) : (
                        <Image
                          fetchPriority={index < 5 ? "high" : "low"}
                          srcSetUrls={{
                            sm: edge?.node?.thumbnailUrl645w ?? "",
                            md: edge?.node?.thumbnailUrl645w ?? "",
                            lg: edge?.node?.thumbnailUrl645w ?? "",
                            xl: edge?.node?.thumbnailUrl645w ?? "",
                          }}
                          alt="Project image"
                          className={"h-full w-full object-contain"}
                          onContextMenu={e => e.preventDefault()}
                        />
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <div>
        <div className="mb-4 mt-3 flex justify-center">
          {!!project?.files?.edges &&
            project?.files?.edges.length > 1 &&
            project?.files?.edges?.map((edge, index) => {
              const isActive = index === visibleIndex
              return (
                <div
                  key={edge?.node?.id || index}
                  style={{
                    width: `${indicatorWidth}px`,
                  }}
                  className={clsx("h-[2px] rounded-full", isActive ? "bg-fg-1" : "bg-bg-3")}
                />
              )
            })}
        </div>
      </div>
    </>
  )
}
